'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { formatDate } from '@/utils/dateUtils';

interface Video {
  id: string;
  thumbnail: string;
  title: string;
  description: string;
  category: string;
  location: string;
  date: string;
  videoUrl: string;
  language: string;
}

interface VideoGalleryProps {
  videos: Video[];
}

const VideoGallery: React.FC<VideoGalleryProps> = ({ videos }) => {
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);

  const closeVideoModal = useCallback(() => setSelectedVideo(null), []);

  // Keyboard close
  useEffect(() => {
    if (!selectedVideo) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeVideoModal();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedVideo, closeVideoModal]);

  return (
    <>
      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {videos.map((video) => (
          <div
            key={video.id}
            className="group relative bg-card rounded-xl overflow-hidden shadow-subtle hover:shadow-elevated transition-all duration-300 cursor-pointer"
            onClick={() => setSelectedVideo(video)}
          >
            <div className="relative aspect-video overflow-hidden">
              <AppImage
                src={video.thumbnail}
                alt={video.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-foreground/40 group-hover:bg-foreground/55 transition-colors duration-300 flex items-center justify-center">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-primary rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Icon name="PlayIcon" size={24} className="text-primary-foreground ml-0.5" />
                </div>
              </div>
            </div>

            <div className="p-3 sm:p-4">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="bg-secondary/10 text-secondary px-2 py-0.5 rounded text-xs font-semibold">
                  {video.category}
                </span>
                {video.language && (
                  <span className="bg-accent/10 text-accent px-2 py-0.5 rounded text-xs font-semibold">
                    {video.language}
                  </span>
                )}
              </div>
              <h4 className="font-heading font-semibold text-sm sm:text-base text-foreground mb-1.5 line-clamp-2">
                {video.title}
              </h4>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                {video.location && (
                  <span className="flex items-center gap-1">
                    <Icon name="MapPinIcon" size={11} />
                    {video.location}
                  </span>
                )}
                {video.date && (
                  <span className="flex items-center gap-1">
                    <Icon name="CalendarIcon" size={11} />
                    {formatDate(video.date)}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
          style={{ background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(6px)' }}
          onClick={closeVideoModal}
        >
          <div
            className="relative bg-card rounded-2xl overflow-hidden shadow-2xl w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeVideoModal}
              className="absolute top-3 right-3 z-10 w-8 h-8 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center transition-colors duration-200"
              aria-label="Close video"
            >
              <Icon name="XMarkIcon" size={18} className="text-white" />
            </button>

            {/* Video Player */}
            <div className="relative aspect-video bg-black">
              <iframe
                width="100%"
                height="100%"
                src={selectedVideo.videoUrl}
                title={selectedVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>

            {/* Info Panel */}
            <div className="p-4 sm:p-5">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="text-base sm:text-lg font-heading font-bold text-foreground leading-snug">
                  {selectedVideo.title}
                </h3>
                <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                  {selectedVideo.category && (
                    <span className="bg-secondary/10 text-secondary px-2 py-0.5 rounded-full text-xs font-semibold">
                      {selectedVideo.category}
                    </span>
                  )}
                  {selectedVideo.language && (
                    <span className="bg-accent/10 text-accent px-2 py-0.5 rounded-full text-xs font-semibold">
                      {selectedVideo.language}
                    </span>
                  )}
                </div>
              </div>

              {selectedVideo.description && (
                <p className="text-sm text-muted-foreground mb-3 leading-relaxed line-clamp-3">
                  {selectedVideo.description}
                </p>
              )}

              <div className="flex items-center gap-4 text-xs text-muted-foreground border-t border-border pt-3">
                {selectedVideo.location && (
                  <span className="flex items-center gap-1.5">
                    <Icon name="MapPinIcon" size={13} className="text-primary" />
                    {selectedVideo.location}
                  </span>
                )}
                {selectedVideo.date && (
                  <span className="flex items-center gap-1.5">
                    <Icon name="CalendarIcon" size={13} className="text-primary" />
                    {formatDate(selectedVideo.date)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VideoGallery;